import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EDIT_PROFILE_MODAL_TYPE } from '../../types/edit-profile-modal-types.enum';
import { User } from '../../types/user.model';
import { AddDisplayedLocationModalComponent } from '../add-displayed-location-modal/add-displayed-location-modal.component';
import { AddProfilePicModalComponent } from '../add-profile-pic-modal/add-profile-pic-modal.component';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
import { EditPersonalDetailsModalComponent } from '../edit-personal-details-modal/edit-personal-details-modal.component';
import { AddDateOfBirthModalComponent } from '../add-date-of-birth-modal/add-date-of-birth-modal.component';
import { AuthService } from '../../../public/auth.service';
import {
  hasAuthEmail,
  hasAuthPhone,
  isAuthEmailVerified,
  isAuthPhoneVerified,
  isNonEmptyAuthString,
} from '../../../shared/membership/auth-user-flags';

@Component({
  selector: 'mmb-web-app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {
  @Input() user: User;
  name: string;
  email: string;
  password: string;

  constructor(private modalService: NgbModal,
    private fb: UntypedFormBuilder, private route: ActivatedRoute,
    private router: Router, private authService: AuthService) { }

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params) {
        if (params.edit === EDIT_PROFILE_MODAL_TYPE.DISPLAYED_LOCATION) {
          this.openAddDisplayedLocationModal();
        }
      }
    });
  }

  openEditProfilePicModal() {
    const modalRef = this.modalService.open(AddProfilePicModalComponent, { centered: true });
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.addSuccess.subscribe((ev) => {
      modalRef.close();
    });
    modalRef.componentInstance.closed.subscribe((ev) => {
      modalRef.close();
    });
  }

  openDateOfBirthModal() {
    const modalRef = this.modalService.open(AddDateOfBirthModalComponent, { centered: true });
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.addSuccess.subscribe((ev) => {
      this.user.birth_date = ev.birth_date;
      modalRef.close();
    });
    modalRef.componentInstance.closed.subscribe((ev) => {
      modalRef.close();
    });
  }

  openEditPersonalDetailsModal() {
    const modalRef = this.modalService.open(EditPersonalDetailsModalComponent, { centered: true });
    let form: UntypedFormGroup = this.fb.group({
      firstName: [this.user.first_name, [Validators.required]],
      lastName: [this.user.last_name, [Validators.required]],
      email: [this.user?.email || this.user?.email_pending || '', [Validators.required, Validators.email]]
    });
    modalRef.componentInstance.form = form;
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.addSuccess.subscribe((ev) => {
      modalRef.close();
      this.user.first_name = ev.firstName;
      this.user.last_name = ev.lastName;
      this.user.email = ev.email;
    });
    modalRef.componentInstance.closed.subscribe((ev) => {
      modalRef.close();
    });
  }

  openChangePasswordModal() {
    const modalRef = this.modalService.open(ChangePasswordModalComponent, { centered: true });
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.addSuccess.subscribe((ev) => {
      modalRef.close();
    });
    modalRef.componentInstance.closed.subscribe((ev) => {
      modalRef.close();
    });
  }

  openAddDisplayedLocationModal() {
    const modalRef = this.modalService.open(AddDisplayedLocationModalComponent, { centered: true });
    modalRef.componentInstance.user = this.user;
    modalRef.componentInstance.addSuccess.subscribe((ev) => {
      this.user.country_current = ev.currentCountry;
      if (ev.currentCity && ev.currentCity.id) {
        this.user.city_current = ev.currentCity;
      } else {
        this.user.city_custom_current = ev.currentCity;
      }
      modalRef.close();
    });
    modalRef.componentInstance.closed.subscribe((ev) => {
      modalRef.close();
    });
  }

  logoutAllDevices() {
    this.authService.logoutAllDevices();
  }

  getDisplayPhone(): string | null {
    if (isNonEmptyAuthString(this.user?.phone_number)) {
      return this.user.phone_number;
    }
    if (isNonEmptyAuthString(this.user?.phone_number_pending)) {
      return this.user.phone_number_pending;
    }
    return null;
  }

  hasPhone(): boolean {
    return hasAuthPhone(this.user);
  }

  hasEmail(): boolean {
    return hasAuthEmail(this.user);
  }

  needsEmailVerify(): boolean {
    return hasAuthEmail(this.user) && !isAuthEmailVerified(this.user);
  }

  needsPhoneVerify(): boolean {
    return isNonEmptyAuthString(this.user?.phone_number_pending) && !isAuthPhoneVerified(this.user);
  }

  navigateToPhoneFlow() {
    this.router.navigate([this.hasPhone() ? '/change-phone' : '/add-phone']);
  }

  navigateToEmailFlow() {
    this.router.navigate([this.hasEmail() ? '/change-email' : '/add-email']);
  }

  getCurrentLocation() {
    let location = this.user.country_current ? this.user.country_current.name : '-';
    if (this.user.city_current) {
      location = `${this.user.city_current.name_ascii}, ${location}`;
    } else if (this.user.city_custom_current) {
      location = `${this.user.city_custom_current}, ${location}`;
    }
    return location;
  }

  getSignupLocation() {
    let location = this.user.country_signup ? this.user.country_signup.name : '-';
    if (this.user.city_signup) {
      location = `${this.user.city_signup.name_ascii}, ${location}`;
    } else if (this.user.city_custom_signup) {
      location = `${this.user.city_custom_signup      }, ${location}`;
    }
    return location;
  }
}
